import * as React from 'react';
import {
  createTheme,
  ThemeProvider,
  StyledEngineProvider,
  alpha,
  lighten,
  adaptV4Theme,
  Theme,
} from '@mui/material/styles';
import {getJointTheme} from "../../helpers/init.helpers";

export default function CustomTheme(props:any): JSX.Element {
  const jointTheme = getJointTheme();
  const [theme] = React.useState(createTheme(adaptV4Theme({
    breakpoints: {
      values: {
        xs: 0,
        sm: 600,
        md: 900,
        lg: 1200,
        xl: 1536,
      },
    },
    overrides: {
      MuiButton: {
        root: {
          textTransform: 'none',
        },
        outlined: {
          borderColor: jointTheme.text_color,
        },
      },
      MuiTextField: {
        root: {
          '& label': {
            color: jointTheme.text_color,
            padding: 0,
            fontSize: '1rem',
            fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
            fontWeight: '400',
            lineHeight: '1',
            letterSpacing: '0.00938em',
          },
        },
      },
      MuiInputBase: {
        root: {
          '& input': {
            border: 0,
            borderColor: 'transparent',
            borderStyle: 'none',
            borderWidth: '0',
            backgroundColor: 'transparent',
            borderRadius: '0',
            padding: '16.5px 14px',
          },
        },
      },
      MuiOutlinedInput: {
        root: {
          position: 'relative',
          '& $notchedOutline': {
            borderColor: jointTheme.text_color,
          },
          '&:hover:not($disabled):not($focused):not($error) $notchedOutline': {
            borderColor: jointTheme.primary_color,
            // Reset on touch devices, it doesn't add specificity
            '@media (hover: none)': {
              borderColor: jointTheme.text_color,
            },
          },
          '&$focused $notchedOutline': {
            borderColor: jointTheme.primary_color,
            borderWidth: 1,
          },
        },
      },
      MuiFormLabel: {
        root: {
          fontWeight: '400!important',
          fontSize: '1rem!important',
          '&$focused': {
            color: jointTheme.primary_color
          }
        }
      },
      MuiTooltip: {
        popper: {
          zIndex: 1298,
        }
      }
    },
    typography: {
      fontSize: 16,
      h1: {
        fontWeight: 400,
        fontSize: "2rem",
        lineHeight: 1.23,
        color: jointTheme.text_color,
      },
      h2: {
        fontWeight: 400,
        fontSize: "1.5rem",
        lineHeight: 1.23,
        color: jointTheme.text_color,
      },
      h3: {
        fontWeight: 500,
        fontSize: "1.25rem",
        lineHeight: 1.23,
        color: jointTheme.text_color,
      },
      h4: {
        fontWeight: 500,
        fontSize: "1.125rem",
        lineHeight: 1.23,
        color: jointTheme.text_color,
      },
      h5: {
        fontWeight: 600,
        fontSize: "1rem",
        lineHeight: 1.23,
        color: jointTheme.text_color,
      },
      h6: {
        fontWeight: 600,
        fontSize: ".875rem",
        lineHeight: 1.23,
        color: jointTheme.text_color,
      },
      body1: {
        fontSize: '1rem',
        lineHeight: 1.43,
        color: jointTheme.text_color,
      },
      body2: {
        fontSize: '.875rem',
        lineHeight: 1.43,
        color: jointTheme.text_color,
      },
      subtitle1: {
        fontWeight: 400,
        fontSize: "1rem",
        lineHeight: 1.43,
        color: alpha(jointTheme.text_color, 0.72),
      },
      subtitle2: {
        fontWeight: 400,
        fontSize: ".875rem",
        lineHeight: 1.43,
        color: alpha(jointTheme.text_color, 0.72),
      }
    },
    palette: {
      primary: {
        light: alpha(jointTheme.primary_color, 0.52),
        main: jointTheme.primary_color,
      },
      secondary: {
        light: alpha(jointTheme.secondary_color, 0.52),
        main: jointTheme.secondary_color,
      },
      text: {
        primary: jointTheme.text_color,
        secondary: alpha(jointTheme.text_color, 0.72),
      },
      divider: alpha(jointTheme.text_color, 0.12),
      background: {
        paper: lighten(jointTheme.background_color, 0.12),
        default: jointTheme.background_color,
      }
    },
    shape: {
      // @ts-ignore
      borderRadius: jointTheme.border_radius,
    },
    props: {
      MuiSvgIcon: {
        htmlColor: jointTheme.text_color,
      }
    }
  })));

  React.useEffect(() => {
    function loadStyles(theme: Theme) {
      const stylesheet = document.getElementById('joint-theme') || document.createElement('style');
      stylesheet.setAttribute('id', 'joint-theme');
      document.head.prepend(stylesheet);
      stylesheet.innerHTML = `
        :root {
          --joint-theme-font: Roboto, Helvetica, Arial, sans-serif;
          --joint-theme-background-default: ${theme.palette.background.default};
          --joint-theme-background-paper: ${theme.palette.background.paper};
          --joint-theme-text-primary: ${theme.palette.text.primary};
          --joint-theme-text-secondary: ${theme.palette.text.secondary};
          --joint-theme-primary-light: ${theme.palette.primary.light};
          --joint-theme-primary-main: ${theme.palette.primary.main};
          --joint-theme-primary-dark: ${theme.palette.primary.dark};
          --joint-theme-primary-contrast-text: ${theme.palette.primary.contrastText};
          --joint-theme-secondary-light: ${theme.palette.secondary.light};
          --joint-theme-secondary-main: ${theme.palette.secondary.main};
          --joint-theme-secondary-dark: ${theme.palette.secondary.dark};
          --joint-theme-secondary-contrast-text: ${theme.palette.secondary.contrastText};
          --joint-theme-success: ${theme.palette.success.dark};
          --joint-theme-warning: ${theme.palette.warning.dark};
          --joint-theme-error: ${theme.palette.error.dark};
          --joint-theme-divider: ${theme.palette.divider};
          --joint-theme-border-radius: ${theme.shape.borderRadius};
          --joint-theme-spacing-1: ${theme.spacing(1)};
          --joint-theme-spacing-2: ${theme.spacing(2)};
          --joint-theme-spacing-3: ${theme.spacing(3)};
          --joint-theme-spacing-4: ${theme.spacing(4)};
          --joint-theme-spacing-5: ${theme.spacing(5)};
          --joint-theme-shadows-3: ${theme.shadows[3]};
        }
      `;
    }
    loadStyles(theme);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={theme}>
        {props.children}
      </ThemeProvider>
    </StyledEngineProvider>
  );
}
